import { Question } from '../types';
import { isNonEmptyString } from './verifyInputs';

const KEY_REGEX = /\b(Q[A-Za-z0-9_]+)\b/g;
const ALLOWED_FORMULA_CHARS = /^[\d\s+\-*/().,Q_A-Za-z0-9]+$/;

export function isOpenNumberQuestion(
  question: Pick<Question, 'question_type' | 'field_type'>,
): boolean {
  return question.question_type === 'text' && question.field_type === 'number';
}

function extractFormulaKeys(formula: string): string[] {
  const matches = formula.match(KEY_REGEX) || [];
  return Array.from(new Set(matches));
}

function hasBalancedParentheses(formula: string): boolean {
  let depth = 0;
  for (const char of formula) {
    if (char === '(') {
      depth += 1;
    }
    if (char === ')') {
      depth -= 1;
      if (depth < 0) {
        return false;
      }
    }
  }
  return depth === 0;
}

export function getFormulaValidationError(
  question: Pick<Question, 'question_type' | 'field_type' | 'formula' | 'question_key'>,
  formulaEnabled: boolean,
  allQuestionKeys: string[],
): string | null {
  if (!isOpenNumberQuestion(question) || !formulaEnabled) {
    return null;
  }

  const formula = question.formula?.trim() ?? '';
  if (!formula) {
    return 'יש להזין נוסחה';
  }

  if (!ALLOWED_FORMULA_CHARS.test(formula)) {
    return 'הנוסחה מכילה תווים לא חוקיים';
  }

  if (!hasBalancedParentheses(formula)) {
    return 'סוגריים לא מאוזנים בנוסחה';
  }

  const referencedKeys = extractFormulaKeys(formula);
  if (question.question_key && referencedKeys.includes(question.question_key)) {
    return 'הנוסחה לא יכולה להתייחס לשאלה עצמה';
  }

  const keySet = new Set(allQuestionKeys);
  for (const key of referencedKeys) {
    if (!keySet.has(key)) {
      return `מפתח שאלה לא קיים: ${key}`;
    }
  }

  return null;
}

export function isFormulaRequiredButMissing(
  question: Pick<Question, 'question_type' | 'field_type' | 'formula'>,
  formulaEnabled: boolean,
): boolean {
  return isOpenNumberQuestion(question) && formulaEnabled && !isNonEmptyString(question.formula);
}
